<template>
  <div
    :class="[
      brandClasses.container,
      {
        'w-full': layout === 'horizontal',
        'f-col': layout === 'vertical',
      },
    ]"
  >
    <base-link aria-hidden="true" class="shrink-0" tabindex="-1" :to="upsell.url" @click="handleClick()">
      <base-picture
        :alt="upsell.name"
        :height="image.height"
        :src="getImageTransformations(upsell.image, image)"
        :width="image.width"
      />
    </base-link>
    <div
      class="f-col between"
      :class="[brandClasses.content, { 'h-full': layout === 'vertical' }]"
      :style="layout === 'vertical' ? { width: pxToRem(image.width) } : undefined"
    >
      <div :class="[{ 'flex between': addToCartButtonStyle === 'icon' }]">
        <vf-link :class="brandClasses.name" :to="upsell.url" :variant="linkVariant" @click="handleClick()">
          {{ upsell.name }}
        </vf-link>
        <base-button
          v-if="addToCartButtonStyle === 'icon'"
          :aria-label="$t.add"
          :class="brandClasses.cta"
          @click="handleAddToCart"
        >
          <span class="h-6 w-6 flex center rounded-xs p-1">
            <vf-icon name="plus" />
          </span>
        </base-button>
      </div>

      <div :class="hasMultipleUpsells || isRange ? brandClasses.footerMultipleUpsells : brandClasses.footer">
        <product-pricing
          :class="brandClasses.price"
          :class-wrapper="brandClasses.priceWrapper"
          :currency
          :price="upsell.price"
          :show-discount-percentage="$feature.showDiscountPercentageOnCatalog"
        />
        <vf-button
          v-if="addToCartButtonStyle === 'text'"
          class="self-start"
          :class="brandClasses.cta"
          :loading
          :size="buttonSize"
          :variant="buttonVariant"
          @click="handleAddToCart"
        >
          {{ $t.productUpsellAddToCart }}
        </vf-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Product, ProductVariants } from '#root/api/clients/product/data-contracts'
import type { ProductClickAction } from '#types/gtm'
import type { ProductUpsell } from '#types/product'
import { ApiErrorCode } from '#root/enums/api'

export type Props = {
  upsell: ProductUpsell
  suppressMiniCart?: boolean
  layout?: 'horizontal' | 'vertical'
  hasMultipleUpsells?: boolean
}

export type Emits = {
  openQuickshop: [product: Product]
  recClick: [id: string, action?: ProductClickAction]
}

const { layout = 'horizontal', hasMultipleUpsells = false, suppressMiniCart, upsell } = defineProps<Props>()
const emit = defineEmits<Emits>()

const {
  addToCartButtonStyle,
  brandClasses,
  buttonSize,
  buttonVariant,
  image,
  linkVariant,
  openQuickshopForColorVariations
} = useAppConfig().components.product.upsell
const { $gtm, $locale, $t } = useNuxtApp()
const currency = useCurrencyCode($locale)
const cart = useCartStore()
const { trackEvent } = useOptimizely()

const loading = ref(false)

const handleClick = (action: ProductClickAction = 'Navigate To PDP') => {
  emit('recClick', upsell.id, action)
  $gtm.push('upsell.onCrossSellDetails', upsell.analyticsId || upsell.id)
}

const isRange = computed(() => upsell.price.lowCurrent !== upsell.price.highCurrent)

const handleAddToCart = async () => {
  loading.value = true

  setInteractionOrigin('up-sell-complete-the-look', true)

  const shouldOpenQuickshop = upsell.multiVariantsProduct
    || (openQuickshopForColorVariations && upsell.showColorVariations)

  if (shouldOpenQuickshop) {
    handleClick('Open Quick Shop')

    emit('openQuickshop', {
      ...upsell,
      currency,
      attributes: [],
      variants: {} as ProductVariants
    })

    loading.value = false
  }
  else {
    try {
      emit('recClick', upsell.id)
      await withRetryAfterTime(() => cart.add(upsell.sku, {
        suppressMiniCart
      }), 2000, [ApiErrorCode.GWP_PRODUCT])
      trackEvent('upsell_product_add', {})
    }
    catch (err) {
      log.error(err)
    }
    finally {
      loading.value = false
      resetInteractionOrigin()
    }
  }
}
</script>
